import { useEffect, useMemo, useState } from "react";

import {
  LOOM_BOOLEAN_CATALOG,
  reduceLoomAuthoringCommand,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
  type LoomSceneNode,
  type LoomTransform,
} from "./loomAuthoringModel";
import { createLoomCommandId, type LoomCommandAcceptance } from "./loomAuthoringHistory";
import { deriveLoomMirrorAttributes } from "./loomMirrorRelations";
import { loomSceneNodeInputs } from "./loomSceneHierarchy";

type BooleanOperation = Extract<LoomSceneNode, { type: "boolean" }>["operation"];
type WrapAction = "move" | "rotate" | "scale";
export type LoomActiveCapability = WrapAction | "mirror" | "boolean" | "make-output" | "delete";

interface ActionCandidate {
  command: LoomAuthoringCommand | null;
  reason: string | null;
}

interface LoomActivePartActionsProps {
  document: LoomAuthoringDocument;
  node: LoomSceneNode;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  onSelect: (nodeId: string) => void;
}

function actionReasonId(nodeId: string, action: LoomActiveCapability): string {
  return `loom-active-action-reason-${nodeId.replace(/[^A-Za-z0-9_-]+/g, "-")}-${action}`;
}

function commandBase(document: LoomAuthoringDocument, suffix: string) {
  return {
    schema: "loom-authoring-command/v1" as const,
    commandId: createLoomCommandId(`gui-active-${suffix}`, document.revision),
    expectedRevision: document.revision,
  };
}

function availableNodeId(document: LoomAuthoringDocument, preferred: string): string {
  if (!document.nodes.some((node) => node.id === preferred)) return preferred;
  let suffix = 2;
  while (document.nodes.some((node) => node.id === `${preferred}-${suffix}`)) suffix += 1;
  return `${preferred}-${suffix}`;
}

function validateCandidate(document: LoomAuthoringDocument, command: LoomAuthoringCommand | null, fallback: string): ActionCandidate {
  if (!command) return { command, reason: fallback };
  try {
    reduceLoomAuthoringCommand(document, command);
    return { command, reason: null };
  } catch (error) {
    return { command: null, reason: error instanceof Error ? error.message : fallback };
  }
}

function wrapCandidate(
  document: LoomAuthoringDocument,
  node: LoomSceneNode,
  action: WrapAction,
): ActionCandidate & { nodeId: string } {
  const transform = action === "move" ? "translate" : action === "scale" ? "uniform-scale" : "rotate";
  const nodeId = availableNodeId(document, `${transform}-${document.revision + 1}`);
  const operation: LoomTransform = action === "move"
    ? { transform: "translate", offsetM: [0.5, 0, 0] }
    : action === "scale"
      ? { transform: "uniform-scale", factor: 1.1 }
      : { transform: "rotate", axis: "z", angleDeg: 5, originM: [0, 0, 0] };
  const command: LoomAuthoringCommand = {
    ...commandBase(document, action),
    kind: "node.attach",
    node: { id: nodeId, label: `${transform} · ${node.label}`, type: "transform", inputNodeId: node.id, operation },
    attachment: { kind: "replace-all-occurrences", targetNodeId: node.id },
  };
  return { ...validateCandidate(document, command, `${action} is unavailable for this entity`), nodeId };
}

function rootedNodeIds(document: LoomAuthoringDocument): Set<string> {
  const byId = new Map(document.nodes.map((node) => [node.id, node]));
  const reached = new Set<string>();
  const visit = (nodeId: string): void => {
    if (reached.has(nodeId)) return;
    reached.add(nodeId);
    const node = byId.get(nodeId);
    if (node) loomSceneNodeInputs(node).forEach(visit);
  };
  document.rootNodeIds.forEach(visit);
  return reached;
}

function mirrorCandidate(document: LoomAuthoringDocument, node: LoomSceneNode): ActionCandidate {
  const attributes = deriveLoomMirrorAttributes(document);
  const selectedPartner = attributes.find((attribute) => attribute.partnerNodeId === node.id);
  const selectedSourceAttributes = attributes.filter((attribute) => attribute.sourceNodeId === node.id);
  if (selectedPartner) return validateCandidate(document, {
    ...commandBase(document, "mirror-off"), kind: "mirror.partner.remove",
    sourceNodeId: selectedPartner.sourceNodeId, partnerNodeId: selectedPartner.partnerNodeId,
  }, "derived mirror cannot be detached safely");
  if (selectedSourceAttributes.length > 1) return { command: null, reason: "Multiple legacy mirror partners require the command API" };
  if (selectedSourceAttributes.length === 1) {
    const partner = selectedSourceAttributes[0]!;
    return validateCandidate(document, {
      ...commandBase(document, "mirror-off"), kind: "mirror.partner.remove",
      sourceNodeId: node.id, partnerNodeId: partner.partnerNodeId,
    }, "mirror partner cannot be detached safely");
  }
  if (node.type !== "primitive" || node.definition.primitive !== "wing") {
    return { command: null, reason: "New mirror partners are currently authored only for native Wing primitives" };
  }
  const reached = rootedNodeIds(document);
  const unions = document.nodes.filter((candidate): candidate is Extract<LoomSceneNode, { type: "boolean" }> => (
    candidate.type === "boolean" && candidate.operation === "union" && reached.has(candidate.id)
    && candidate.inputNodeIds.includes(node.id)
  ));
  const partnerNodeId = availableNodeId(document, `${node.id}-mirror`);
  const attach = unions.length === 1
    ? { kind: "existing-union" as const, booleanNodeId: unions[0]!.id }
    : unions.length === 0 && document.rootNodeIds.includes(node.id)
      ? {
          kind: "replace-root" as const,
          unionNodeId: availableNodeId(document, `mirror-union-${document.revision + 1}`),
          unionLabel: `${node.label} pair`,
        }
      : null;
  if (!attach) return { command: null, reason: "Wing must have one unambiguous rooted union or be a Pattern output" };
  return validateCandidate(document, {
    ...commandBase(document, "mirror-on"), kind: "mirror.partner.create", sourceNodeId: node.id,
    partner: { nodeId: partnerNodeId, label: `${node.label} reflected surface` },
    plane: { frame: "world", axis: "y", offsetM: 0 }, attach,
  }, "mirror partner cannot be attached safely");
}

function booleanCandidate(
  document: LoomAuthoringDocument,
  node: LoomSceneNode,
  operation: BooleanOperation,
  otherNodeId: string,
): ActionCandidate & { nodeId: string } {
  const nodeId = availableNodeId(document, `${operation}-${document.revision + 1}`);
  if (!otherNodeId || otherNodeId === node.id) return { command: null, nodeId, reason: "Choose another rooted operand" };
  const command: LoomAuthoringCommand = {
    ...commandBase(document, "boolean"), kind: "node.attach",
    node: {
      id: nodeId, label: `${operation} · ${node.label}`, type: "boolean", operation,
      inputNodeIds: [node.id, otherNodeId],
      ...(operation === "smooth-union" ? { blendRadiusM: 0.1 } : {}),
    },
    attachment: { kind: "replace-all-occurrences", targetNodeId: node.id },
  };
  return { ...validateCandidate(document, command, "Boolean wrapping is unavailable"), nodeId };
}

function validBooleanOperands(
  document: LoomAuthoringDocument,
  node: LoomSceneNode,
  operation: BooleanOperation,
): readonly LoomSceneNode[] {
  return document.nodes.filter((candidate) => (
    candidate.id !== node.id
    && candidate.type !== "import"
    && booleanCandidate(document, node, operation, candidate.id).command !== null
  ));
}

export function deriveLoomActiveCapabilities(
  document: LoomAuthoringDocument,
  node: LoomSceneNode,
): readonly LoomActiveCapability[] {
  const capabilities: LoomActiveCapability[] = [];
  (["move", "rotate", "scale"] as const).forEach((action) => {
    if (wrapCandidate(document, node, action).command) capabilities.push(action);
  });
  if (mirrorCandidate(document, node).command) capabilities.push("mirror");
  if (LOOM_BOOLEAN_CATALOG.some(({ id }) => validBooleanOperands(document, node, id).length > 0)) capabilities.push("boolean");
  const output = document.rootNodeIds.length === 1 && document.rootNodeIds[0] === node.id
    ? { command: null, reason: "Already the sole Pattern output" }
    : validateCandidate(document, {
        ...commandBase(document, "output"), kind: "roots.set", nodeIds: [node.id],
      }, "This entity cannot become the sole output without breaking dependencies");
  if (output.command) capabilities.push("make-output");
  const mirror = deriveLoomMirrorAttributes(document).find((attribute) => attribute.partnerNodeId === node.id);
  const removeCommand: LoomAuthoringCommand = mirror
    ? {
        ...commandBase(document, "delete-mirror"), kind: "mirror.partner.remove",
        sourceNodeId: mirror.sourceNodeId, partnerNodeId: mirror.partnerNodeId,
      }
    : { ...commandBase(document, "delete"), kind: "node.remove", nodeId: node.id };
  if (validateCandidate(document, removeCommand, "Entity is still referenced").command) capabilities.push("delete");
  return capabilities;
}

export function LoomActivePartActions({ document, node, onCommand, onSelect }: LoomActivePartActionsProps) {
  const [booleanOperation, setBooleanOperation] = useState<BooleanOperation>("union");
  const operands = useMemo(
    () => validBooleanOperands(document, node, booleanOperation),
    [booleanOperation, document, node],
  );
  const [otherNodeId, setOtherNodeId] = useState(operands[0]?.id ?? "");
  useEffect(() => {
    if (!operands.some((operand) => operand.id === otherNodeId)) setOtherNodeId(operands[0]?.id ?? "");
  }, [operands, otherNodeId]);
  const capabilities = useMemo(() => deriveLoomActiveCapabilities(document, node), [document, node]);
  const mirror = useMemo(() => mirrorCandidate(document, node), [document, node]);
  const mirrorAttribute = deriveLoomMirrorAttributes(document).find((attribute) => (
    attribute.sourceNodeId === node.id || attribute.partnerNodeId === node.id
  ));
  const output = document.rootNodeIds.length === 1 && document.rootNodeIds[0] === node.id
    ? { command: null, reason: "Already the sole Pattern output" }
    : validateCandidate(document, {
        ...commandBase(document, "output"), kind: "roots.set", nodeIds: [node.id],
      }, "This entity cannot become the sole output without breaking dependencies");
  const linkedMirror = deriveLoomMirrorAttributes(document).find((attribute) => attribute.partnerNodeId === node.id);
  const remove = validateCandidate(document, linkedMirror ? {
    ...commandBase(document, "delete-mirror"), kind: "mirror.partner.remove",
    sourceNodeId: linkedMirror.sourceNodeId, partnerNodeId: linkedMirror.partnerNodeId,
  } : { ...commandBase(document, "delete"), kind: "node.remove", nodeId: node.id }, "Entity is still referenced");
  const execute = (candidate: ActionCandidate, subjectIds: readonly string[], nextSelection?: string) => {
    if (!candidate.command) return;
    const error = onCommand(candidate.command, { origin: "gui.scene", subjectIds });
    if (!error && nextSelection) onSelect(nextSelection);
  };
  const transforms = (["move", "rotate", "scale"] as const).map((action) => ({
    action,
    candidate: wrapCandidate(document, node, action),
  }));
  const booleanAction = booleanCandidate(document, node, booleanOperation, otherNodeId);
  const descriptions: readonly { action: LoomActiveCapability; reason: string | null }[] = [
    ...transforms.map(({ action, candidate }) => ({ action, reason: candidate.reason })),
    { action: "mirror", reason: mirror.reason },
    { action: "boolean", reason: booleanAction.reason },
    { action: "make-output", reason: output.reason },
    { action: "delete", reason: remove.reason },
  ];
  const constraints = descriptions.flatMap(({ reason }) => reason ? [reason] : []);
  const mirrorSubjectIds = mirror.command?.kind === "mirror.partner.create"
    ? [mirror.command.sourceNodeId, mirror.command.partner.nodeId,
        mirror.command.attach.kind === "existing-union" ? mirror.command.attach.booleanNodeId : mirror.command.attach.unionNodeId]
    : mirrorAttribute ? [mirrorAttribute.sourceNodeId, mirrorAttribute.partnerNodeId] : [node.id];

  return (
    <section
      aria-label={`Actions for ${node.label}`}
      className="loom-active-actions"
      data-atomic-command-schema="loom-authoring-command/v1"
      data-capabilities={capabilities.join(" ")}
      data-node-id={node.id}
      data-revision={document.revision}
      data-testid="loom-active-actions"
    >
      <header><span>Active part actions</span><strong>{capabilities.length} available</strong></header>
      <div className="loom-active-transform-actions">
        {transforms.map(({ action, candidate }) => {
          return <button
            aria-describedby={candidate.reason ? actionReasonId(node.id, action) : undefined}
            data-action={action}
            disabled={!candidate.command}
            key={action}
            onClick={() => execute(candidate, [node.id, candidate.nodeId], candidate.nodeId)}
            title={candidate.reason ?? `Wrap ${node.label} in one atomic ${action} transform`}
            type="button"
          >{action === "move" ? "Move" : action === "rotate" ? "Rotate" : "Scale"}</button>;
        })}
        <button
          aria-describedby={mirror.reason ? actionReasonId(node.id, "mirror") : undefined}
          aria-pressed={Boolean(mirrorAttribute)}
          data-action="mirror"
          disabled={!mirror.command}
          onClick={() => execute(mirror, mirrorSubjectIds)}
          title={mirror.reason ?? "Toggle one explicit derived mirror entity"}
          type="button"
        >Mirror</button>
      </div>
      <div className="loom-active-boolean-action">
        <select aria-label="Active part Boolean operation" onChange={(event) => setBooleanOperation(event.target.value as BooleanOperation)} value={booleanOperation}>
          {LOOM_BOOLEAN_CATALOG.map(({ id }) => <option key={id} value={id}>{id}</option>)}
        </select>
        <select aria-label="Active part Boolean operand" disabled={!operands.length} onChange={(event) => setOtherNodeId(event.target.value)} value={otherNodeId}>
          {operands.map((operand) => <option key={operand.id} value={operand.id}>{operand.label}</option>)}
        </select>
        <button
          aria-describedby={booleanAction.reason ? actionReasonId(node.id, "boolean") : undefined}
          data-action="boolean"
          disabled={!booleanAction.command}
          onClick={() => execute(booleanAction, [node.id, otherNodeId, booleanAction.nodeId], booleanAction.nodeId)}
          title={booleanAction.reason ?? "Create one atomic Boolean wrapper"}
          type="button"
        >Boolean</button>
      </div>
      <div className="loom-active-structural-actions">
        <button aria-describedby={output.reason ? actionReasonId(node.id, "make-output") : undefined} data-action="make-output" disabled={!output.command} onClick={() => execute(output, [node.id])} title={output.reason ?? "Make this the sole Pattern output"} type="button">Make output</button>
        <button aria-describedby={remove.reason ? actionReasonId(node.id, "delete") : undefined} data-action="delete" disabled={!remove.command} onClick={() => execute(remove, linkedMirror ? [linkedMirror.sourceNodeId, node.id] : [node.id], linkedMirror?.sourceNodeId)} title={remove.reason ?? "Delete through one validated atomic command"} type="button">Delete</button>
      </div>
      {constraints.length ? <small data-testid="loom-active-action-constraints">Unavailable actions preserve rooted references and explicit mirror/source invariants.</small> : null}
      {descriptions.map(({ action, reason }) => reason ? (
        <span className="loom-sr-only" id={actionReasonId(node.id, action)} key={action}>{reason}</span>
      ) : null)}
    </section>
  );
}
